import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";

/**
 * Public page renderer.
 * Fetches the HTML stored for the given slug and mounts it via srcDoc so
 * CSP headers from the parent app don't interfere with the embedded content.
 * No authentication required.
 */
export default function PublicPage() {
  const { slug } = useParams();
  const [html, setHtml] = useState("");
  const [meta, setMeta] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | not_found | error

  useEffect(() => {
    const load = async () => {
      try {
        const [metaRes, htmlRes] = await Promise.all([
          axios.get(`${API}/public-pages/by-slug/${slug}`),
          axios.get(`${API}/public-pages/by-slug/${slug}/html`),
        ]);
        setMeta(metaRes.data);
        setHtml(htmlRes.data);
        document.title = metaRes.data?.name || "PD Travel";
        setStatus("ready");
      } catch (err) {
        if (err.response?.status === 404) setStatus("not_found");
        else setStatus("error");
      }
    };
    load();
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-400 text-sm">Cargando...</p>
      </div>
    );
  }

  if (status === "not_found") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="text-center max-w-md">
          <p className="text-4xl mb-2">🔍</p>
          <h1 className="text-xl font-semibold text-slate-700">Página no encontrada</h1>
          <p className="text-sm text-slate-500 mt-1">El enlace ya no está disponible o expiró.</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="text-center max-w-md">
          <p className="text-4xl mb-2">⚠</p>
          <h1 className="text-xl font-semibold text-slate-700">No se pudo cargar la página</h1>
          <p className="text-sm text-slate-500 mt-1">Inténtalo de nuevo en unos minutos.</p>
        </div>
      </div>
    );
  }

  return (
    <iframe
      title={meta?.name || "Public page"}
      srcDoc={html}
      sandbox="allow-scripts allow-forms allow-popups allow-same-origin allow-modals"
      className="fixed inset-0 w-screen h-screen border-0"
      style={{ width: "100vw", height: "100vh" }}
      data-testid="public-page-iframe"
    />
  );
}
